import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Search, Download, Plus,
  ArrowUp, ArrowDown, ChevronsUpDown,
  ChevronLeft, ChevronRight, Users, HardHat, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import type { SellerRegistrationStatus, SellerRegistrationItem } from '@m8-app/contracts';
import { getSellerDevices } from '../api/seller.js';
import { StatusBadge } from '../components/ui/Badge.js';
import { TableSkeleton, EmptyState } from '../components/ui/index.js';
import { cn } from '../components/ui/cn.js';
import { inferProductType, type ProductType } from '../lib/product.js';

const PAGE_SIZE = 10;
type SortCol = 'serialNumber' | 'registeredAt';
type SortDir = 'asc' | 'desc';
type ProductFilter = ProductType | 'all';

const WARRANTY_YEARS = 2;
const WARRANTY_SOON_DAYS = 90;

function warrantyExpiry(registeredAt: string): Date {
  const d = new Date(registeredAt);
  d.setFullYear(d.getFullYear() + WARRANTY_YEARS);
  return d;
}

/**
 * Prefer the backend-computed expiry (devices.warranty_expires_at) when it
 * exists — that value reflects the current admin warranty-config setting and
 * for M8 lights uses the activation timestamp, not seller registration. Fall
 * back to `2 years from warrantyStartAt/registeredAt` for legacy rows that
 * predate the migration.
 */
function warrantyStatus(
  registeredAt: string,
  warrantyStartAt?: string | null,
  warrantyExpiresAt?: string | null,
): { label: string; daysLeft: number; expired: boolean; soon: boolean } {
  const exp = warrantyExpiresAt
    ? new Date(warrantyExpiresAt)
    : warrantyExpiry(warrantyStartAt ?? registeredAt);
  const now = new Date();
  const msLeft = exp.getTime() - now.getTime();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  const expired = daysLeft <= 0;
  const soon = !expired && daysLeft <= WARRANTY_SOON_DAYS;
  const label = expired
    ? 'Expired'
    : soon
      ? `${daysLeft}d left`
      : new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(exp);
  return { label, daysLeft, expired, soon };
}

function fmtDate(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

const STATUS_OPTIONS: { value: SellerRegistrationStatus | 'all'; label: string }[] = [
  { value: 'all',       label: 'All'        },
  { value: 'pending',   label: 'Registered' },
  { value: 'activated', label: 'Activated'  },
];


function Th({ children, onClick, active, dir, sortable = false }: {
  children: React.ReactNode; onClick?: () => void;
  active?: boolean; dir?: SortDir; sortable?: boolean;
}) {
  return (
    <th
      onClick={onClick}
      className={cn(
        'text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide',
        sortable && 'cursor-pointer select-none hover:text-slate-700 transition-colors',
      )}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortable && (
          active
            ? dir === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-500" /> : <ArrowDown className="w-3 h-3 text-blue-500" />
            : <ChevronsUpDown className="w-3 h-3 text-slate-300" />
        )}
      </div>
    </th>
  );
}

export function DeviceListPage() {
  const navigate      = useNavigate();
  const { pathname }  = useLocation();

  // The sidebar entry IS the product selection: each of the three lists
  // shows exactly its own product, so there is no product filter on the page.
  const lockedProduct: ProductFilter =
    pathname === '/devices/cop-lop' ? 'cop_lop' :
    pathname === '/devices/m8'      ? 'm8'      :
    pathname === '/devices/avira'   ? 'avira'   :
    'all';

  const registerTo =
    lockedProduct === 'cop_lop' ? '/devices/register/cop-lop' :
    lockedProduct === 'm8'      ? '/devices/register/m8'      :
    lockedProduct === 'avira'   ? '/devices/register/avira'   :
    null;

  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState<SellerRegistrationStatus | 'all'>('all');
  const productFilter: ProductFilter = lockedProduct;
  const [sort, setSort]                   = useState<{ col: SortCol; dir: SortDir }>({ col: 'registeredAt', dir: 'desc' });
  const [page, setPage]                   = useState(1);

  const { data, isLoading, isError, error } = useQuery({ queryKey: ['seller-devices'], queryFn: getSellerDevices });

  useEffect(() => {
    if (isError) toast.error((error as AxiosError<{ message?: string }>)?.response?.data?.message ?? 'Failed to load devices.');
  }, [isError, error]);

  useEffect(() => { setPage(1); }, [search, statusFilter, productFilter]);

  function toggleSort(col: SortCol) {
    setSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  }

  const registrations = (data?.registrations ?? []) as SellerRegistrationItem[];

  const filtered = registrations.filter((r) => {
    const q = search.toLowerCase();
    const product = inferProductType(r.serialNumber);
    const matchesSearch = q === ''
      || r.serialNumber.toLowerCase().includes(q)
      || r.customerMobile.includes(q)
      || (r.customerName ?? '').toLowerCase().includes(q);
    return matchesSearch
      && (statusFilter === 'all'  || r.status === statusFilter)
      && (productFilter === 'all' || product   === productFilter);
  });

  const sorted = [...filtered].sort((a, b) => {
    const cmp = sort.col === 'serialNumber'
      ? a.serialNumber.localeCompare(b.serialNumber)
      : new Date(a.registeredAt).getTime() - new Date(b.registeredAt).getTime();
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged      = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function exportCsv() {
    const rows = [
      ['#', 'Serial Number', 'Status', 'Registered'],
      ...filtered.map((r, i) => [
        String(i + 1),
        r.serialNumber,
        r.status,
        new Intl.DateTimeFormat('en-GB').format(new Date(r.registeredAt)),
      ]),
    ];
    const csv  = rows.map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'haron-devices.csv' });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} device${filtered.length !== 1 ? 's' : ''}`);
  }

  const pageTitle =
    lockedProduct === 'cop_lop' ? 'COP-LOP Devices' :
    lockedProduct === 'm8'      ? 'M8 Light Devices' :
    lockedProduct === 'avira'   ? 'Avira Devices'    :
    'All Devices';

  const pageSubtitle =
    lockedProduct === 'cop_lop' ? 'All registered COP-LOP (lift) devices.' :
    lockedProduct === 'm8'      ? 'All registered M8 (stair light) devices.' :
    lockedProduct === 'avira'   ? 'All registered Avira (ceiling light) devices.' :
    'All registered devices across all product types.';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900">{pageTitle}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{pageSubtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {registerTo && (
            <button
              type="button"
              onClick={() => navigate(registerTo)}
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg transition-all shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" />
              Register
            </button>
          )}
          <button
            type="button" onClick={exportCsv} disabled={filtered.length === 0}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 bg-white px-3 py-2 rounded-lg hover:border-slate-300 transition-all disabled:opacity-40"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search serial, customer name or mobile…"
            className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          {STATUS_OPTIONS.map((s) => (
            <button key={s.value} type="button" onClick={() => setStatusFilter(s.value)}
              className={cn('px-3 py-1.5 rounded-md text-xs font-semibold transition-all',
                statusFilter === s.value ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700')}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <TableSkeleton rows={6} cols={4} />}
      {!isLoading && sorted.length === 0 && (
        <EmptyState
          variant={registrations.length === 0 ? 'no-devices' : 'no-results'}
          registerHref={registerTo ?? '/devices/register/cop-lop'}
        />
      )}

      {!isLoading && paged.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>
                <Th>#</Th>
                <Th sortable active={sort.col === 'serialNumber'} dir={sort.dir} onClick={() => toggleSort('serialNumber')}>Serial Number</Th>
                <Th>Status</Th>
                <Th sortable active={sort.col === 'registeredAt'} dir={sort.dir} onClick={() => toggleSort('registeredAt')}>Registered</Th>
                <Th>Warranty</Th>
                <Th>Users</Th>
                <Th>Installer</Th>
              </tr>
            </thead>
            <tbody>
              {paged.map((item, i) => {
                const globalIndex = (page - 1) * PAGE_SIZE + i + 1;
                const userCount   = item.customerMobile ? 1 : 0;
                const installer   = item.installerName || '—';
                const warranty    = warrantyStatus(item.registeredAt, item.warrantyStartAt, item.warrantyExpiresAt);

                return (
                  <motion.tr
                    key={item.registrationId}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ duration: 0.12, delay: i * 0.02 }}
                    className={cn('transition-colors', i < paged.length - 1 && 'border-b border-slate-50')}
                  >
                    {/* # */}
                    <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums w-10">
                      {globalIndex}
                    </td>

                    {/* Serial Number — click → device history */}
                    <td className="px-5 py-3.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/devices/${item.registrationId}`)}
                        className="font-mono text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline underline-offset-2 transition-colors text-left"
                      >
                        {item.serialNumber}
                      </button>
                      {item.customerName && (
                        <p className="text-xs text-slate-400 mt-0.5">{item.customerName}</p>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-5 py-3.5">
                      <StatusBadge status={item.status} />
                    </td>

                    {/* Registration Date */}
                    <td className="px-5 py-3.5 text-xs text-slate-600 tabular-nums whitespace-nowrap">
                      {fmtDate(item.registeredAt)}
                    </td>

                    {/* Warranty */}
                    <td className="px-5 py-3.5">
                      {warranty.expired ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md bg-red-50 text-red-600 ring-1 ring-red-200/60">
                          <AlertTriangle className="w-3 h-3" /> Expired
                        </span>
                      ) : warranty.soon ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 ring-1 ring-amber-200/60">
                          <AlertTriangle className="w-3 h-3" /> {warranty.label}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60">
                          <ShieldCheck className="w-3 h-3" /> {warranty.label}
                        </span>
                      )}
                    </td>

                    {/* Users — click → users page filtered by device */}
                    <td className="px-5 py-3.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/users?device=${item.registrationId}&serial=${encodeURIComponent(item.serialNumber)}`)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-blue-600 bg-slate-100 hover:bg-blue-50 px-2.5 py-1.5 rounded-lg transition-all"
                      >
                        <Users className="w-3.5 h-3.5" />
                        {userCount} user{userCount !== 1 ? 's' : ''}
                      </button>
                    </td>

                    {/* Installer — click → installer profile */}
                    <td className="px-5 py-3.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/installers?name=${encodeURIComponent(installer)}&device=${item.registrationId}`)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-blue-600 hover:underline underline-offset-2 transition-all text-left"
                      >
                        <HardHat className="w-3.5 h-3.5 text-slate-400" />
                        {installer}
                      </button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-400">
              {sorted.length === 0 ? '0 results'
                : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, sorted.length)} of ${sorted.length}`}
            </span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors" aria-label="Previous">
                <ChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <span className="px-2.5 text-xs font-semibold text-slate-600 tabular-nums">{page}/{totalPages}</span>
              <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}
                className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors" aria-label="Next">
                <ChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
